import React from 'react'
import { ClientOnly, createFileRoute } from '@tanstack/react-router'
import {
  CalendarIcon,
  ClipboardListIcon,
  DownloadIcon,
  FileJsonIcon,
  FileSpreadsheetIcon,
  FilterIcon,
  HardDriveIcon,
  PlusIcon,
  SearchIcon,
  XIcon,
} from 'lucide-react'

import {
  useJobLog,
  type ApplicationStatus,
  type WorkMode,
} from '@/components/hooks/use-job-log'
import {
  JobEntryDialog,
  type JobEntryFormData,
} from '@/components/job-log/job-entry-dialog'
import {
  JobLogTable,
  type SortConfig,
} from '@/components/job-log/job-log-table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export const Route = createFileRoute('/_index/log')({
  component: () => (
    <ClientOnly>
      <RouteComponent />
    </ClientOnly>
  ),
})

type DateFilter =
  | 'all'
  | 'today'
  | 'this-week'
  | 'this-month'
  | 'last-7-days'
  | 'last-30-days'

const dateFilterOptions: { value: DateFilter; label: string }[] = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'this-week', label: 'This Week' },
  { value: 'this-month', label: 'This Month' },
  { value: 'last-7-days', label: 'Last 7 Days' },
  { value: 'last-30-days', label: 'Last 30 Days' },
]

const statusFilterOptions: {
  value: ApplicationStatus | 'all'
  label: string
}[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'applied', label: 'Applied' },
  { value: 'interviewing', label: 'Interviewing' },
  { value: 'offered', label: 'Offered' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'withdrawn', label: 'Withdrawn' },
]

const workModeFilterOptions: { value: WorkMode | 'all'; label: string }[] = [
  { value: 'all', label: 'All Work Modes' },
  { value: 'remote', label: 'Remote' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'onsite', label: 'On-site' },
]

function getDateRange(filter: DateFilter): { start: Date; end: Date } | null {
  if (filter === 'all') return null

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const end = new Date(today)
  end.setDate(end.getDate() + 1) // End of today

  switch (filter) {
    case 'today':
      return { start: today, end }
    case 'this-week': {
      const dayOfWeek = today.getDay()
      const start = new Date(today)
      start.setDate(start.getDate() - dayOfWeek) // Start of week (Sunday)
      return { start, end }
    }
    case 'this-month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1)
      return { start, end }
    }
    case 'last-7-days': {
      const start = new Date(today)
      start.setDate(start.getDate() - 6)
      return { start, end }
    }
    case 'last-30-days': {
      const start = new Date(today)
      start.setDate(start.getDate() - 29)
      return { start, end }
    }
    default:
      return null
  }
}

function RouteComponent() {
  const {
    applications,
    addApplication,
    updateApplication,
    deleteApplication,
    exportToCSV,
    exportToJSON,
  } = useJobLog()

  const [addDialogOpen, setAddDialogOpen] = React.useState(false)

  // Filter state
  const [searchQuery, setSearchQuery] = React.useState('')
  const [dateFilter, setDateFilter] = React.useState<DateFilter>('all')
  const [statusFilter, setStatusFilter] = React.useState<
    ApplicationStatus | 'all'
  >('all')
  const [workModeFilter, setWorkModeFilter] = React.useState<WorkMode | 'all'>(
    'all',
  )

  // Sort state
  const [sortConfig, setSortConfig] = React.useState<SortConfig>({
    key: 'date',
    direction: 'desc',
  })

  const handleAddApplication = (data: JobEntryFormData) => {
    addApplication(data)
    setAddDialogOpen(false)
  }

  const clearFilters = () => {
    setSearchQuery('')
    setDateFilter('all')
    setStatusFilter('all')
    setWorkModeFilter('all')
  }

  const hasActiveFilters =
    searchQuery !== '' ||
    dateFilter !== 'all' ||
    statusFilter !== 'all' ||
    workModeFilter !== 'all'

  // Filter and sort applications
  const filteredAndSortedApplications = React.useMemo(() => {
    let filtered = [...applications]

    // Search filter (position, company, location, notes)
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (app) =>
          app.position.toLowerCase().includes(query) ||
          app.company.toLowerCase().includes(query) ||
          app.location.toLowerCase().includes(query) ||
          app.notes.toLowerCase().includes(query),
      )
    }

    // Date filter
    const dateRange = getDateRange(dateFilter)
    if (dateRange) {
      filtered = filtered.filter((app) => {
        if (!app.date) return false
        const appDate = new Date(app.date)
        return appDate >= dateRange.start && appDate < dateRange.end
      })
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((app) => app.status === statusFilter)
    }

    // Work mode filter
    if (workModeFilter !== 'all') {
      filtered = filtered.filter((app) => app.workMode === workModeFilter)
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue: string | number = ''
      let bValue: string | number = ''

      switch (sortConfig.key) {
        case 'date':
          aValue = a.date ? new Date(a.date).getTime() : 0
          bValue = b.date ? new Date(b.date).getTime() : 0
          break
        case 'position':
          aValue = a.position.toLowerCase()
          bValue = b.position.toLowerCase()
          break
        case 'company':
          aValue = a.company.toLowerCase()
          bValue = b.company.toLowerCase()
          break
        case 'status':
          aValue = a.status
          bValue = b.status
          break
        case 'workMode':
          aValue = a.workMode
          bValue = b.workMode
          break
        case 'location':
          aValue = a.location.toLowerCase()
          bValue = b.location.toLowerCase()
          break
        case 'salary':
          aValue = a.salary.toLowerCase()
          bValue = b.salary.toLowerCase()
          break
        default:
          return 0
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })

    return filtered
  }, [
    applications,
    searchQuery,
    dateFilter,
    statusFilter,
    workModeFilter,
    sortConfig,
  ])

  const activeFilterCount = [
    dateFilter !== 'all',
    statusFilter !== 'all',
    workModeFilter !== 'all',
  ].filter(Boolean).length

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="mb-2 text-3xl font-bold tracking-tight">
            Job Application Log
          </h1>
          <p className="text-muted-foreground">
            Track and manage all your job applications in one place
          </p>
        </div>
        <div className="flex gap-2">
          {applications.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={(props) => (
                  <Button {...props} variant="outline" className="gap-2">
                    <DownloadIcon className="h-4 w-4" />
                    Export
                  </Button>
                )}
              />
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={exportToCSV}>
                  <FileSpreadsheetIcon className="mr-2 h-4 w-4" />
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={exportToJSON}>
                  <FileJsonIcon className="mr-2 h-4 w-4" />
                  Export as JSON
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <JobEntryDialog
            open={addDialogOpen}
            onOpenChange={setAddDialogOpen}
            onSave={handleAddApplication}
            trigger={
              <Button className="gap-2">
                <PlusIcon className="h-4 w-4" />
                Add Application
              </Button>
            }
          />
        </div>
      </div>

      {applications.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-6 py-16">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
              <ClipboardListIcon className="h-7 w-7 text-muted-foreground" />
            </div>
            <div className="space-y-2 text-center">
              <h2 className="text-2xl font-semibold tracking-tight">
                No Applications Yet
              </h2>
              <p className="max-w-md text-muted-foreground">
                Start tracking your job applications here. You can add entries
                manually or log them after tailoring your resume.
              </p>
            </div>
            <JobEntryDialog
              onSave={handleAddApplication}
              trigger={
                <Button size="lg" className="gap-2">
                  <PlusIcon className="h-4 w-4" />
                  Add Your First Application
                </Button>
              }
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Filter Bar */}
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-wrap items-center gap-3">
                {/* Search */}
                <div className="relative min-w-64 flex-1">
                  <SearchIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search position, company, location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>

                {/* Date Filter */}
                <Select
                  value={dateFilter}
                  onValueChange={(value) => setDateFilter(value as DateFilter)}
                >
                  <SelectTrigger className="w-40" data-placeholder="Date range">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {dateFilterOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Status Filter */}
                <Select
                  value={statusFilter}
                  onValueChange={(value) =>
                    setStatusFilter(value as ApplicationStatus | 'all')
                  }
                >
                  <SelectTrigger className="w-40" data-placeholder="Status">
                    <FilterIcon className="mr-2 h-4 w-4" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusFilterOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Work Mode Filter */}
                <Select
                  value={workModeFilter}
                  onValueChange={(value) =>
                    setWorkModeFilter(value as WorkMode | 'all')
                  }
                >
                  <SelectTrigger className="w-40" data-placeholder="Work mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {workModeFilterOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Clear Filters */}
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="gap-1"
                  >
                    <XIcon className="h-4 w-4" />
                    Clear
                    {activeFilterCount > 0 && (
                      <Badge variant="secondary" className="ml-1">
                        {activeFilterCount}
                      </Badge>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Results Count & Storage Notice */}
          <div className="flex flex-wrap items-center justify-between gap-2 px-1">
            <div className="text-sm text-muted-foreground">
              Showing {filteredAndSortedApplications.length} of{' '}
              {applications.length} applications
              {hasActiveFilters && ' (filtered)'}
            </div>
            <div className="flex items-center gap-1.5 rounded-lg bg-muted px-3 py-1.5 text-xs text-muted-foreground">
              <HardDriveIcon className="h-3 w-3" />
              <span>
                Data stored locally.{' '}
                <button
                  onClick={exportToJSON}
                  className="font-medium underline underline-offset-2 hover:text-foreground"
                >
                  Export
                </button>{' '}
                before clearing browser data.
              </span>
            </div>
          </div>

          {/* Table */}
          {filteredAndSortedApplications.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center gap-4 py-12">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                  <SearchIcon className="h-6 w-6 text-muted-foreground" />
                </div>
                <div className="space-y-1 text-center">
                  <h3 className="text-lg font-medium">No Results Found</h3>
                  <p className="text-sm text-muted-foreground">
                    No applications match your current filters.
                  </p>
                </div>
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="overflow-hidden py-0">
              <JobLogTable
                applications={filteredAndSortedApplications}
                onUpdate={updateApplication}
                onDelete={deleteApplication}
                sortConfig={sortConfig}
                onSortChange={setSortConfig}
              />
            </Card>
          )}
        </div>
      )}
    </div>
  )
}
